import {
  CombinedApiData,
  LayoutCountData,
  ParticipantStatus,
  ActionableInfo, // New
  Stakeholder,    // New
  ClinicDevice,    // New (ensure it's imported if not already)
  ParticipantAlert,
  AlertType
} from '../interfaces/participantTrackerTypes';
import { parseApiDate } from './dateUtils';
import { addDays, isAfter, differenceInDays, subDays } from 'date-fns';
import moment from 'moment';

export const getParticipantStatus = (
  rowData: CombinedApiData | undefined,
  layoutData: LayoutCountData | undefined
): ParticipantStatus => {
  if (!rowData) {
    return ParticipantStatus.UNKNOWN;
  }

  const actualSv3DateExists = !!parseApiDate(rowData.actual_sv3_date);
  const actualSv2DateExists = !!parseApiDate(rowData.actual_sv2_date);
  const screeningDateExists = !!parseApiDate(rowData['Screening Date']);

  if (actualSv3DateExists) {
    return ParticipantStatus.ACTIVE_HOME;
  }

  if (actualSv2DateExists) {
    const hasHomeLayout = layoutData?.layouts_json?.some(layout =>
      layout.name.toLowerCase().includes('home')
    ) ?? false;

    if (hasHomeLayout) {
      return ParticipantStatus.AWAITING_BASELINE;
    } else {
      return ParticipantStatus.AWAITING_LAYOUT;
    }
  }

  if (screeningDateExists) {
    return ParticipantStatus.SCREENING_AWAIT_SV2;
  }

  return ParticipantStatus.UNKNOWN;
};

export const getActionableInfo = (
  rowData: CombinedApiData | undefined,
  layoutData: LayoutCountData | undefined,
  devicesForParticipant: ClinicDevice[] // Devices already filtered for this participant
): ActionableInfo => {
  // Pre-checks: Fundamental data / VVP system setup issues
  if (!rowData || !rowData.Subject) { // rowData.Subject is key from REGN/CRO CSV
    return { nextAction: "Awaiting Data from CRO/REGN", responsible: "REGN" };
  }

  // NEW CHECK 1: VVP Participant Profile Creation by CRC PM
  // If rowData.clinic_name is N/A, it means the participant (from REGN/CRO data)
  // isn't properly set up or linked under a "SIENNA_XXXXXX" clinical account in the VVP system.
  if (!rowData.clinic_name || String(rowData.clinic_name).toUpperCase() === 'N/A') {
    return { nextAction: "Create Participant Profile", responsible: "CRC" };
  }

  // From this point, we assume a VVP participant profile exists and is linked to a site.
  const status = getParticipantStatus(rowData, layoutData); // Determine current clinical status

  // NEW CHECK 2: VVP Device Assignment by CRC PM
  // If no device (HMD icon) is associated with the participant in the VVP system dashboard.
  // This is indicated by devicesForParticipant array being empty.
  const hasTrainingOrLocalizerLayout = layoutData?.layouts_json?.some(
    layout => layout.name.toLowerCase().includes('training') || layout.name.toLowerCase().includes('localizer')
  ) ?? false;
  const hasHomeLayout = layoutData?.layouts_json?.some(layout => layout.name.toLowerCase().includes('home')) ?? false;

  if (devicesForParticipant.length === 0) { // No device assigned in VVP system for this participant
    // This assignment is critical before CRC can design layouts tied to a device or Site can use a device for SV2.
    if (status === ParticipantStatus.SCREENING_AWAIT_SV2 && !hasTrainingOrLocalizerLayout) {
      return { nextAction: "Assign Device", responsible: "CRC" };
    }
    // If SV2 is done and awaiting Home layout, a device must be assigned.
    if (status === ParticipantStatus.AWAITING_LAYOUT && !hasHomeLayout) {
      return { nextAction: "Assign Device", responsible: "CRC" };
    }
    // If participant is somehow ACTIVE_HOME but no device record, this is a critical VVP system setup issue for CRC PM.
    if (status === ParticipantStatus.ACTIVE_HOME) {
        return { nextAction: "Assign Device", responsible: "CRC" };
    }
    // Potentially other states where device assignment is a prerequisite by CRC PM in VVP.
  }

  // Main status-driven clinical workflow actions
  // (These assume fundamental VVP system setup by CRC PM is either done or handled by above checks)
  switch (status) {
    case ParticipantStatus.UNKNOWN:
      // If profile and device assignment (if needed at this stage) are okay, but status is still UNKNOWN,
      // it's likely a data integrity issue or a scenario not yet covered.
      return { nextAction: "Review Data Integrity/Workflow (Status Unknown)", responsible: "VV/REGN" };

    case ParticipantStatus.SCREENING_AWAIT_SV2:
      // If we reach here, VVP profile exists.
      // If device was needed for layout (and !hasTrainingOrLocalizerLayout), the check above would assign to CRC PM.
      // So, if still no layouts, it's CRC's turn to design (assuming device is now assigned or wasn't a blocker).
      if (!hasTrainingOrLocalizerLayout) {
        return { nextAction: "Design Training/Localizer Layouts" + " " , responsible: "VV/CRC" };
      } else {
        // Layouts exist.
        if (devicesForParticipant.length === 0) {
            // This case implies layouts were made *without* a device assigned, which might be an issue,
            // or device assignment is not strictly needed for *these* layouts.
            // However, SV2 needs a device. Given the user rule, if no device icon, CRC PM action.
            // This might conflict if layouts are truly ready. Let's assume device needed for SV2.
            return { nextAction: "Assign Device", responsible: "CRC" };
        }
        const hasTrainingTestData = rowData.training_test_unix_times && rowData.training_test_unix_times.length > 0;
        const hasLocalizerTestData = rowData.localizer_test_unix_times && rowData.localizer_test_unix_times.length > 0;

        if( !hasTrainingTestData && !hasLocalizerTestData) {
          return { nextAction: "Administer Training & Localizer Tests", responsible: "Site" };
        }

        if (hasTrainingTestData && !hasLocalizerTestData) {
          return { nextAction: "Administer Localizer Test", responsible: "Site" };
        }


        return { nextAction: "Administer Training & Localizer Tests", responsible: "Site" };
      }

    case ParticipantStatus.AWAITING_LAYOUT: // SV2 is done, Localizer submitted
      if(!rowData.localizer_test_unix_times || rowData.localizer_test_unix_times.length == 0) {
        return { nextAction: "Administer Localizer Test", responsible: "Site" };
      }

      // If we reach here, VVP profile exists.
      // If device was needed for home layout, the check above would assign to CRC PM.
      // So, it's CRC's turn to review Localizer and design Home layout.
      if (!hasHomeLayout) {
        return { nextAction: "Grade Localizer, Configure Home Layout", responsible: "CRC" };
      } else {
        // Home layout exists, but status is still AWAITING_LAYOUT. This might be a status calculation issue or a brief state.
        // The next logical step would be AWAITING_BASELINE.
        return { nextAction: "Conduct SV3 Baseline (first Home)", responsible: "Site" }; // Or "Review Status Logic"
      }


    case ParticipantStatus.AWAITING_BASELINE: // Home Layout is ready
      // Device should be assigned in VVP by now (CRC PM's task).
      // Site needs to ensure physical device is ready for dispensation.
      if (devicesForParticipant.length === 0) {
         // This shouldn't happen if previous CRC PM steps were completed. Indicates a VVP system setup issue.
         return { nextAction: "Assign Device", responsible: "CRC" };
      }
      return { nextAction: "Administer Home Test & Dispense Device", responsible: "Site" };

    case ParticipantStatus.ACTIVE_HOME:{
      // VVP system device assignment by CRC PM should be done.
      // Now it's about the physical device being active and used.
      const activeDevice = devicesForParticipant.find(d => d.home_device_is_active === '1');
      
      if (!activeDevice) {
        // No *active* home device. This could be because:
        // 1. Device record exists in VVP (CRC PM did their job) but physical hardware not yet activated by PV/Site.
        // 2. No device record at all (missed by earlier CRC PM checks - unlikely if logic is sound, but a fallback).
        if (devicesForParticipant.length > 0) { // VVP record exists, but not active
            return { nextAction: "Activate/Provision Physical Home Device", responsible: "PV/Site"};
        } else { // Should have been caught earlier by CRC PM device assignment.
            return { nextAction: "Assign Device in VVP Dashboard (Participant Active)", responsible: "CRC" };
        }
      }
      
      const lastLogin = activeDevice.home_device_time_last_login;
      if (!lastLogin || lastLogin.startsWith('0000-')) {
        return { nextAction: "Verify Device Setup & Participant First Login", responsible: "PV/Site" };
      }
      // Future: Add logic for stale login here (e.g., no login in X days).
      return { nextAction: "Monitor Home Bundle Progress", responsible: "VV/Site" };
    }
    default:
      // This case should ideally not be reached if all statuses are handled.
      return { nextAction: "N/A - Review Participant Status Logic", responsible: "VV/REGN" };
  }
};

// Helper to check if a projected visit window is overdue
const isVisitWindowOverdue = (projectedEndDateStr: string | number | null | undefined, actualDateStr: string | number | null | undefined): boolean => {
  if (actualDateStr || !projectedEndDateStr) return false; // Has actual or no end date
  const projectedEndDate = parseApiDate(projectedEndDateStr);
  return projectedEndDate ? isAfter(new Date(), projectedEndDate) : false;
};

// Helper to check stale login
const isDeviceLoginStale = (lastLoginStr: string | null | undefined, daysThreshold = 7): boolean => {
  if (!lastLoginStr || lastLoginStr.startsWith('0000-')) return false; // Never logged in is different from stale
  const lastLoginDate = moment.utc(lastLoginStr).toDate();
  return differenceInDays(new Date(), lastLoginDate) > daysThreshold;
};


export const getParticipantAlerts = (
  rowData: CombinedApiData | undefined,
  layoutData: LayoutCountData | undefined,
  devicesForParticipant: ClinicDevice[]
): ParticipantAlert[] => {
  const alerts: ParticipantAlert[] = [];
  if (!rowData) return alerts;

  const status = getParticipantStatus(rowData, layoutData);

  // --- VVP System Setup Alerts (from getActionableInfo logic) ---
  if (!rowData.clinic_name || String(rowData.clinic_name).toUpperCase() === 'N/A') {
    alerts.push({ type: AlertType.PROFILE_NOT_CREATED, message: 'VVP Participant profile needs creation by CRC PM.', severity: 'error' });
  }
  // Only add device not assigned if profile IS created
  if (rowData.clinic_name && String(rowData.clinic_name).toUpperCase() !== 'N/A' && devicesForParticipant.length === 0) {
     if (status === ParticipantStatus.SCREENING_AWAIT_SV2 || status === ParticipantStatus.AWAITING_LAYOUT || status === ParticipantStatus.AWAITING_BASELINE || status === ParticipantStatus.ACTIVE_HOME) {
        alerts.push({ type: AlertType.DEVICE_NOT_ASSIGNED_CRITICAL, message: 'No device assigned in VVP by CRC PM for a critical stage.', severity: 'error' });
     }
  }

  // --- Clinical Visit Overdue Alerts ---
  if (isVisitWindowOverdue(rowData['Projected Visit 2 Last Day'], rowData.actual_sv2_date)) {
    alerts.push({ type: AlertType.OVERDUE_VISIT_SV2, message: 'Projected SV2 window has passed.', severity: 'error' });
  }
  if (isVisitWindowOverdue(rowData['Projected Baseline Last Day'], rowData.actual_sv3_date)) {
    alerts.push({ type: AlertType.OVERDUE_VISIT_SV3, message: 'Projected SV3/Baseline window has passed.', severity: 'error' });
  }

  // --- Layout Pending Alert ---
  if (status === ParticipantStatus.AWAITING_LAYOUT) {
    const sv2Date = parseApiDate(rowData.actual_sv2_date);
    // Alert if SV2 was more than X days ago and still no Home layout
    if (sv2Date && differenceInDays(new Date(), sv2Date) > 3) { // Example: 3 days threshold
      alerts.push({ type: AlertType.NO_HOME_LAYOUT_POST_SV2, message: 'Home Layout configuration pending >3 days post-SV2.', severity: 'warning' });
    }
  }

  // --- Device Stale Login Alert ---
  if (status === ParticipantStatus.ACTIVE_HOME) {
    const activeDevice = devicesForParticipant.find(d => d.home_device_is_active === '1');
    if (activeDevice && isDeviceLoginStale(activeDevice.home_device_time_last_login, 7)) { // Example: 7 days
      alerts.push({ type: AlertType.DEVICE_OFFLINE_STALE, message: 'Device has not logged in for over 7 days.', severity: 'warning' });
    }
  }

  // --- Low Adherence Alert (Placeholder - needs data source) ---
  // if (status === ParticipantStatus.ACTIVE_HOME && rowData.home_bundle_progress < 0.5 && /* bundle window ending soon */) {
  //   alerts.push({ type: AlertType.LOW_ADHERENCE_HOME, message: 'Low home test adherence for current bundle.', severity: 'warning' });
  // }

  return alerts;
};